import { Link } from "react-router-dom";
import { Users, Scale, MessageCircleHeart, ArrowRight } from "lucide-react";

const FILONI = [
  {
    key: "comunita",
    icon: Users,
    title: "Comunità e reti tra pari",
    hero: "Creiamo spazi in cui incontrarsi, condividere esperienze e diventare risorsa le une per le altre.",
    body: "Non ci sostituiamo ai servizi né agli esperti. Costruiamo occasioni in cui genitori, adulti, bambine e bambini possano incontrarsi, raccontarsi e scoprire che le fatiche personali sono spesso questioni condivise. Da questi incontri nascono legami, mutuo aiuto e piccole reti stabili che continuano a esistere anche fuori dai nostri eventi.",
    quote: "Le cose migliori si costruiscono insieme, non si erogano.",
  },
  {
    key: "parita",
    icon: Scale,
    title: "Parità e libertà dalla violenza",
    hero: "Lavoriamo sui rapporti di potere, sui ruoli di genere, sulla condivisione della cura e sulla prevenzione della violenza.",
    body: "Riconosciamo che la violenza di genere non è un fatto privato: è un fenomeno sociale che si costruisce nelle relazioni quotidiane, negli stereotipi, nella distribuzione ineguale della cura. Portiamo la conversazione nei luoghi ordinari — scuole, piazze, gruppi di parola — e proviamo a smontare, insieme, quello che ci sembra naturale ma naturale non è.",
    quote: "Non è una faccenda privata.",
  },
  {
    key: "cnv",
    icon: MessageCircleHeart,
    title: "CNV e relazioni educative",
    hero: "Promuoviamo ascolto, empatia e strumenti nonviolenti nelle relazioni e nei contesti educativi.",
    body: "La Comunicazione Nonviolenta è per noi un riferimento culturale e una pratica: non una certificazione, non una tecnica magica. Ci aiuta a stare nei conflitti senza fuggire e senza fare male, a educare senza obbedienza e senza permissivismo, ad ascoltare quello che c'è sotto le parole. Sperimentiamo questi strumenti nei laboratori con adulti, nei gruppi di parola, nella formazione con insegnanti ed educatori.",
    quote: "Ascolto, empatia, responsabilità.",
  },
];

export default function IlNostroApproccio() {
  return (
    <div data-testid="approccio-page">
      <section className="relative bg-[#FFF1E8] py-20 lg:py-24 overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 blob bg-[#FF6100]/12 -z-0" />
        <div className="container-vv relative">
          <div className="eyebrow">Il nostro approccio</div>
          <h1 className="mt-4 font-display font-medium text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#292522] max-w-4xl">
            Tre fili che tengono insieme <span className="text-[#FF6100]">Vola Vola.</span>
          </h1>
          <p className="mt-6 text-lg text-[#292522]/80 max-w-3xl leading-relaxed">
            Vola Vola fa molte cose diverse, ma dietro ai nostri progetti ci sono tre fili che ritornano sempre: costruire relazioni e reti tra pari, lavorare per la parità e contro la violenza di genere, diffondere un modo nonviolento di stare nelle relazioni e di educare. Cambiano i format; il punto di partenza resta lo stesso.
          </p>
        </div>
      </section>

      {FILONI.map((f, idx) => {
        const Icon = f.icon;
        const alt = idx % 2 === 1;
        return (
          <section key={f.key} id={f.key} className={`py-20 lg:py-24 ${alt ? "bg-[#F8F1E8]" : "bg-[#FFFCF8]"}`}>
            <div className="container-vv grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-5">
                <div className="h-14 w-14 rounded-2xl bg-[#FF6100]/12 flex items-center justify-center text-[#FF6100] mb-5">
                  <Icon className="h-7 w-7" strokeWidth={1.5} />
                </div>
                <div className="eyebrow">Filo {idx + 1} / 3</div>
                <h2 className="mt-3 font-display font-medium text-4xl sm:text-5xl text-[#292522] leading-tight">{f.title}</h2>
                <p className="mt-6 text-lg font-display italic text-[#FF6100] leading-snug">{f.hero}</p>
              </div>
              <div className="lg:col-span-7 space-y-5">
                <p className="text-lg text-[#292522]/85 leading-relaxed">{f.body}</p>
                <blockquote className="border-l-4 border-[#FF6100] pl-5 py-1 text-[#292522]/70 italic">{f.quote}</blockquote>
                <div className="pt-2">
                  <Link to={`/progetti?filone=${f.key}`} className="btn-ghost text-sm">Progetti collegati a questo filo <ArrowRight className="h-4 w-4" strokeWidth={1.75} /></Link>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <section className="bg-[#292522] text-[#FFFCF8] py-20 lg:py-24">
        <div className="container-vv text-center max-w-3xl mx-auto">
          <div className="eyebrow text-[#FF6100]">Dall'intreccio</div>
          <h2 className="mt-3 font-display font-medium text-4xl sm:text-5xl">
            È dall'intreccio di questi tre fili che nascono i nostri <span className="text-[#FF6100]">progetti.</span>
          </h2>
          <p className="mt-6 text-[#FFFCF8]/70 text-lg leading-relaxed">
            Non sono tre compartimenti separati. Un laboratorio di comunicazione in una scuola è insieme comunità tra pari, prevenzione della violenza e pratica nonviolenta. Un gruppo di parola tra genitori attraversa tutti e tre i fili contemporaneamente. Cambia lo sfondo, cambiano le persone, ma la trama resta la stessa.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/progetti" className="btn-primary">Scopri i nostri progetti <ArrowRight className="h-4 w-4" strokeWidth={1.75} /></Link>
            <Link to="/eventi" className="btn-ghost !bg-transparent !text-[#FFFCF8] !border-[#FFFCF8]/30 hover:!text-[#FF6100] hover:!border-[#FF6100]">Vieni a un evento</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
